// src/hooks/useTrainingLibrary.js
// Fix: useCategoriesWithCount now exposes refetch()

import { useState, useEffect, useCallback } from 'react';
import { supabase, getYouTubeThumbnail } from '../lib/supabase';

function normalizeVideo(row, progressMap = {}) {
  const prog    = progressMap[row.id] || {};
  const ytThumb = row.youtube_id ? getYouTubeThumbnail(row.youtube_id) : null;
  return {
    id:           row.id,
    title:        row.title,
    description:  row.description || '',
    youtubeUrl:   row.youtube_url,
    youtubeId:    row.youtube_id,
    thumbnail:    row.thumbnail_url || ytThumb || '',
    thumbnailAlt: row.title,
    duration:     row.duration_seconds || 0,
    category:     row.video_categories?.name  || '',
    categorySlug: row.video_categories?.slug  || '',
    categoryId:   row.category_id,
    views:        row.training_video_stats?.view_count || 0,
    progress:     prog.progress_percent ?? 0,
    isCompleted:  prog.is_completed     ?? false,
    isBookmarked: prog.is_bookmarked    ?? false,
    isNew:        row.is_new            ?? false,
    uploadDate:   row.upload_date       || row.created_at?.slice(0, 10),
  };
}

// ─────────────────────────────────────────────────────────────
// useCategoriesWithCount — now returns refetch()
// ─────────────────────────────────────────────────────────────
export function useCategoriesWithCount() {
  const [categories, setCategories] = useState([
    { id: 'all', slug: 'all', name: 'Tous', count: 0 },
  ]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('video_categories')
      .select('id, slug, name, icon_name, training_videos(id)')
      .eq('is_active', true)
      .order('name');

    if (!error && data) {
      const items = data.map((c) => ({
        id:        c.slug,
        slug:      c.slug,
        name:      c.name,
        icon_name: c.icon_name || 'Folder',
        count:     Array.isArray(c.training_videos) ? c.training_videos.length : 0,
      }));
      const total = items.reduce((acc, i) => acc + i.count, 0);
      setCategories([{ id: 'all', slug: 'all', name: 'Tous', count: total }, ...items]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  return { categories, loading, refetch: fetchCategories };
}

// ─────────────────────────────────────────────────────────────
// useVideos
// ─────────────────────────────────────────────────────────────
export function useVideos({
  categorySlug = 'all',
  searchQuery  = '',
  sortBy       = 'recent',
} = {}) {
  const [videos, setVideos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      let query = supabase
        .from('training_videos')
        .select(`
          id, title, description, youtube_url, youtube_id,
          thumbnail_url, duration_seconds, category_id,
          is_new, upload_date, created_at,
          video_categories ( id, slug, name, icon_name ),
          training_video_stats ( view_count )
        `)
        .eq('is_active', true);

      if (searchQuery?.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      if (sortBy === 'recent') query = query.order('upload_date', { ascending: false });
      else if (sortBy === 'title') query = query.order('title', { ascending: true });
      else query = query.order('upload_date', { ascending: false });

      const { data: rawVideos, error: vErr } = await query;
      if (vErr) throw vErr;

      let progressMap = {};
      if (user && rawVideos?.length) {
        const ids = rawVideos.map((v) => v.id);
        const { data: progRows } = await supabase
          .from('training_video_progress')
          .select('video_id, progress_percent, is_completed, is_bookmarked')
          .eq('user_id', user.id)
          .in('video_id', ids);
        if (progRows) {
          progressMap = Object.fromEntries(progRows.map((r) => [r.video_id, r]));
        }
      }

      let normalized = (rawVideos || []).map((r) => normalizeVideo(r, progressMap));

      if (categorySlug && categorySlug !== 'all') {
        normalized = normalized.filter((v) => v.categorySlug === categorySlug);
      }
      if (sortBy === 'popular')  normalized.sort((a, b) => b.views    - a.views);
      if (sortBy === 'duration') normalized.sort((a, b) => b.duration - a.duration);

      setVideos(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, [categorySlug, searchQuery, sortBy]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  return { videos, loading, error, refetch: fetchVideos };
}

// ─────────────────────────────────────────────────────────────
// usePlaylists
// ─────────────────────────────────────────────────────────────
export function usePlaylists() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    async function fetch() {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('training_playlists')
        .select(`id, title, training_playlist_videos(video_id, training_videos(duration_seconds))`)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (!error && data) {
        let progressMap = {};
        if (user) {
          const { data: pp } = await supabase
            .from('training_playlist_progress')
            .select('playlist_id, progress_percent')
            .eq('user_id', user.id);
          if (pp) progressMap = Object.fromEntries(pp.map((r) => [r.playlist_id, r.progress_percent]));
        }
        setPlaylists(data.map((pl) => {
          const pvs = pl.training_playlist_videos || [];
          const sec = pvs.reduce((a, pv) => a + (pv.training_videos?.duration_seconds || 0), 0);
          const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
          return { id: pl.id, title: pl.title, videoCount: pvs.length,
            duration: h > 0 ? `${h}h ${m}min` : `${m}min`, progress: progressMap[pl.id] ?? 0 };
        }));
      }
      setLoading(false);
    }
    fetch();
  }, []);

  return { playlists, loading };
}

// ─────────────────────────────────────────────────────────────
// useStats
// ─────────────────────────────────────────────────────────────
export function useStats() {
  const [stats, setStats] = useState({ totalVideos: 0, completedVideos: 0, watchTime: '0min', progress: 0 });

  useEffect(() => {
    async function fetch() {
      const { data: { user } } = await supabase.auth.getUser();
      const [{ count: total }, progResult, watchResult] = await Promise.all([
        supabase.from('training_videos').select('id', { count: 'exact', head: true }).eq('is_active', true),
        user ? supabase.from('training_video_progress').select('is_completed').eq('user_id', user.id)
             : Promise.resolve({ data: [] }),
        user ? supabase.from('training_video_progress')
                 .select('progress_percent, training_videos(duration_seconds)').eq('user_id', user.id)
             : Promise.resolve({ data: [] }),
      ]);
      const completed = (progResult.data || []).filter((r) => r.is_completed).length;
      const sec = (watchResult.data || []).reduce(
        (a, r) => a + Math.round(((r.progress_percent || 0) / 100) * (r.training_videos?.duration_seconds || 0)), 0
      );
      const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
      setStats({ totalVideos: total || 0, completedVideos: completed,
        watchTime: h > 0 ? `${h}h ${m}min` : `${m}min`,
        progress: total ? Math.round((completed / total) * 100) : 0 });
    }
    fetch();
  }, []);

  return stats;
}

// ─────────────────────────────────────────────────────────────
// useVideoActions
// ─────────────────────────────────────────────────────────────
export function useVideoActions() {
  const incrementView = useCallback(async (videoId) => {
    await supabase.rpc('increment_training_view', { p_video_id: videoId });
  }, []);

  const saveProgress = useCallback(async (videoId, progressPercent, isCompleted = false) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.rpc('upsert_training_progress', {
      p_user_id: user.id, p_video_id: videoId,
      p_progress: progressPercent, p_is_completed: isCompleted,
    });
  }, []);

  const toggleBookmark = useCallback(async (videoId, currentState) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.rpc('upsert_training_progress', {
      p_user_id: user.id, p_video_id: videoId,
      p_progress: 0, p_is_completed: false, p_is_bookmarked: !currentState,
    });
  }, []);

  return { incrementView, saveProgress, toggleBookmark };
}